import { DOCUMENTS_TYPE, TENANTS } from "../../../../constants"
import { getCountryFromLanguage } from "../../../../core/service/BusinessLogicService"
import { loc } from "../../../../core/utils"
import Select from "../../../../form/Select"
import { useFetchUploadDocumentTypes, useUploadDocuments } from "../../../../hooks"
import { TunnelDataContext } from "../../../context/TunnelDataContext"
import decorator from "../../decorator/decorator"
import uploadFileValidator from "../../validator/UploadFileValidator"
import { setIn } from "final-form"
import arrayMutators from "final-form-arrays"
import createDecorator from "final-form-calculate"
import React from "react"
import { Button, Col, Row } from "react-bootstrap"
import Modal from "react-bootstrap/Modal"
import * as FinalForm from "react-final-form"
import { FieldArray } from "react-final-form-arrays"
import { ClipLoader } from "react-spinners"

const focusOnErrors = createDecorator()
const maximumFileUpload = 12
let defaultDocumentTypeOption = DOCUMENTS_TYPE.WAY_OUT_DOCUMENT

const getDefaultSupportingDocumentDeliveryForm = (documentTypesOptions) => {
  const defaultOption = documentTypesOptions?.filter(
    (documentTypes) => documentTypes.name === defaultDocumentTypeOption
  )

  return defaultOption?.[0].id
}

export default function ImportFileUploader() {
  const { tunnelData } = React.useContext(TunnelDataContext)
  defaultDocumentTypeOption =
    tunnelData.tenant === TENANTS.RETAIL_IT ? DOCUMENTS_TYPE.WAY_OUT_DOCUMENT : DOCUMENTS_TYPE.DELIVERY_FORM

  const handleDecorator = React.useMemo(() => decorator(), [])

  const hiddenFileInput = Array(maximumFileUpload)
    .fill(0)
    .map(() => React.createRef())

  const [showUploadDoc, setShowUploadDoc] = React.useState(false)
  const { documentTypesOptions, isLoadingDocumentTypes } = useFetchUploadDocumentTypes()

  const documentTypesFiltered = filterDocumentsForRetailIt(documentTypesOptions)

  function filterDocumentsForRetailIt(documentTypesOptions) {
    if (tunnelData.tenant === TENANTS.RETAIL_IT) {
      return documentTypesOptions?.filter((x) => x.name === "INVOICE" || x.name === "WAY_OUT_DOCUMENT")
    } else {
      return documentTypesOptions
    }
  }

  const { mutate, isLoadingUpload } = useUploadDocuments(setShowUploadDoc, documentTypesFiltered)

  const country = getCountryFromLanguage(tunnelData?.data?.order?.orderLanguageTag)

  const handleImportFile = (index) => {
    hiddenFileInput[index].current.click()
  }

  const handleFilesUpload = (values) => {
    mutate({ datas: { ...values } })
  }

  const validateFields = async (values) => {
    let hasError = false

    const errors = await uploadFileValidator({ ...values, countryLanguage: country }).catch((invalideFields) => {
      hasError = true
      return invalideFields.inner
        ?.filter((field) => field.errors.length > 0)
        .reduce((errors, errorField) => setIn(errors, errorField.path, errorField.message), {})
    })
    return hasError ? errors : null
  }

  return (
    <>
      <Button
        onClick={() => setShowUploadDoc(true)}
        variant="primary"
        className="ico-import-file-before button-center button-rental-voucher"
      >
        {loc("final_page_import_document")}
      </Button>

      <Modal
        show={showUploadDoc}
        centered
        scrollable
        keyboard={false}
        aria-labelledby="upload-document-modal"
        backdrop="static"
        onHide={() => setShowUploadDoc(false)}
        className="modal-content-upload-file"
        dialogClassName="modal-90w"
      >
        <FinalForm.Form
          decorators={[handleDecorator, focusOnErrors]}
          keepDirtyOnReinitialize
          mutators={{
            ...arrayMutators
          }}
          validate={validateFields}
          initialValues={{
            typeOfDocument: {
              value: getDefaultSupportingDocumentDeliveryForm(documentTypesFiltered),
              label: loc(defaultDocumentTypeOption)
            }
          }}
          onSubmit={handleFilesUpload}
          render={({
            form: {
              mutators: { push }
            },
            values,
            hasValidationErrors,
            handleSubmit
          }) => {
            const hasLoadedAllPages = !values?.documentPages?.some((page) => page === undefined)

            return (
              <>
                <Modal.Header closeButton className={"modal-upload-file-header brand"}>
                  <Modal.Title>{`${loc("upload_file_title")}`}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                  <Select
                    name={`typeOfDocument`}
                    label={loc("document_type")}
                    options={documentTypesFiltered?.map((item) => {
                      return {
                        value: item.id,
                        label: loc(item.name)
                      }
                    })}
                    oneElement={true}
                    maxMenuHeight={120}
                    disabled={isLoadingDocumentTypes}
                    isClearable={false}
                  />

                  <Col lg={12}>
                    <FieldArray name="documentPages">
                      {({ fields }) => (
                        <>
                          {fields.map((name, index) => {
                            return (
                              <FinalForm.Field key={name} name={`${name}`}>
                                {({ meta }) => {
                                  const metaError = meta.error && meta.dirty
                                  const hasError = metaError || meta.submitError
                                  return (
                                    <>
                                      <Row
                                        className={
                                          "mt-2 mb-2 ml-0 mr-0 justify-content-between align-items-center sm-wrap"
                                        }
                                      >
                                        <Col md={2} className={"pl-0 pr-0"}>
                                          <p className={"label"}>{`${loc("upload_line_label")} ${index + 1}`}</p>
                                        </Col>
                                        <Col md={1} className={"delete-file-icon"}>
                                          {fields.length > 1 && (
                                            <i
                                              className="fas fa-trash-alt"
                                              onClick={() => {
                                                fields.remove(index)
                                              }}
                                              style={{ cursor: "pointer" }}
                                            />
                                          )}
                                        </Col>
                                      </Row>
                                      <Row className={"mt-2 mb-2 ml-0 mr-0 justify-content-between align-items-center"}>
                                        <Col md={5} className={"margin-top-bottom"}>
                                          <Button variant="outline-primary" onClick={() => handleImportFile(index)}>
                                            {loc("upload_button_line")}
                                            <input
                                              name={`${name}.file`}
                                              type="file"
                                              accept=".jpg, .pdf"
                                              hidden
                                              ref={hiddenFileInput[index]}
                                              onChange={({ target }) => {
                                                fields.update(index, {
                                                  file: target.files[0]
                                                })
                                              }}
                                            />
                                          </Button>
                                        </Col>
                                        <Col md={6} className={"margin-top-bottom"}>
                                          <h4>{`${
                                            values?.documentPages?.[index]?.file?.name || loc("upload_line_no_upload")
                                          }`}</h4>
                                        </Col>
                                      </Row>
                                      {hasError && <p className="required-message">{meta.error || meta.submitError}</p>}
                                      <hr />
                                    </>
                                  )
                                }}
                              </FinalForm.Field>
                            )
                          })}
                          {values?.typeOfDocument && fields.length < 12 && (
                            <Row>
                              <div className="buttons">
                                <Col>
                                  <button type="button" className="add-product" onClick={() => push("documentPages")}>
                                    {loc("upload_button_add_document")}
                                  </button>
                                </Col>
                              </div>
                            </Row>
                          )}
                        </>
                      )}
                    </FieldArray>
                  </Col>
                </Modal.Body>

                <Modal.Footer>
                  <Row className="justify-content-center">
                    {isLoadingUpload ? (
                      <ClipLoader size={35} />
                    ) : (
                      <Col lg={6}>
                        <Button
                          id="confirm-button"
                          variant="success"
                          className={"ico-upload-file-before btn-mt-mb"}
                          onClick={handleSubmit}
                          disabled={hasValidationErrors || !hasLoadedAllPages || isLoadingUpload}
                        >
                          {loc("button_send_upload")}
                        </Button>
                      </Col>
                    )}
                  </Row>
                </Modal.Footer>
              </>
            )
          }}
        />
      </Modal>
    </>
  )
}
